import React, { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import styled from 'styled-components';
import { RadioGroup } from '@material-ui/core';
import { createStructuredSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { autopayIcon } from 'images';
import EditCreditCardDrawer from 'containers/PrimoProfile/BillingInformation/CreditCardForm/EditCreditCardDrawer';
import EditBankAccountDrawer from 'containers/PrimoProfile/BillingInformation/BankAccountForm/EditBankAccountDrawer';
import {
  Button,
  Radio,
  Note,
  AddButton,
  H1,
  H2,
  Div,
  F,
  SuccessBlock,
} from 'components/Ui';
import {
  selectCreditCards,
  selectBankAccounts,
  selectCurrentPaymentMethod,
  selectAutopayUpdatingSuccess,
} from 'containers/PrimoProfile/selectors';
import { showDrawer } from 'containers/DrawerManager/actions';
import { EDIT_CREDIT_CARD } from 'containers/PrimoProfile/BillingInformation/constants';
import {
  updateCreditCard,
  resetAddCardSucessful,
  fetchPaymentMethods,
} from 'containers/PrimoProfile/actions';
import { themeConfig } from 'theme/themeConfig';
import warning from 'images/Warning.png';
import { dataLayerPush } from 'utils/tracking';
import { isCardExpired } from 'containers/PrimoProfile/Payment/utils';

import styles from './styles.scss';

const PrimaryBlue = styled.span`
  color: ${themeConfig.colors.primaryBlue};
`;

const renderPaymentOption = ({ creditCard }) => {
  const label = (
    <span className={styles.label}>
      <F>Saved credit card:</F>{' '}
      <PrimaryBlue>****{creditCard.last4}</PrimaryBlue>
    </span>
  );

  return (
    <div key={creditCard.id} className={styles.checkbox}>
      <Radio
        value={creditCard.id}
        label={label}
        disabled={isCardExpired(creditCard)}
      />
      <div className={styles.additionalInfo}>
        {creditCard.cardholderName}
        <br />
        <F>Expires</F> {creditCard.expirationMonth}/{creditCard.expirationYear}
      </div>
    </div>
  );
};

renderPaymentOption.propTypes = {
  creditCard: PropTypes.object,
};

const AutopaySignUp = (props) => {
  const [pickedPaymentMethod, setPickedPaymentMethod] = useState(
    props.currentPaymentMethod ? props.currentPaymentMethod.id : null,
  );

  const handleChange = ({ target: { value } }) => {
    setPickedPaymentMethod(value);
  };

  const showCreditCardForm = () => {
    props.resetAddCardSucessful();
    props.showDrawer(EDIT_CREDIT_CARD);
  };

  const signUpAutopay = () => {
    const pickedCreditCard = props.creditCards.find(
      (card) => card.id === pickedPaymentMethod,
    );
    if (pickedCreditCard) {
      props.updateCreditCard({ ...pickedCreditCard, autopay: true });
    }

    dataLayerPush(
      'SelfServe',
      {
        event: 'autopay-signup',
        method: 'CC',
      },
      'dlS20',
    );
  };

  useEffect(() => {
    props.fetchPaymentMethods();
  }, []);

  const showWoops = isEmpty(props.creditCards) && isEmpty(props.bankAccounts);
  const isAutopaySuccessful = useSelector(selectAutopayUpdatingSuccess());
  return (
    <Fragment>
      {!isAutopaySuccessful ? (
        <>
          <img src={autopayIcon} className={styles.autopayIcon} alt="" />
          <H1>Sign up for easy AutoPay</H1>
          <Div className={styles.info} translationKey={'AutoPayMeans'}>
            With AutoPay, you no longer need to worry about making payments on
            time. All you have to do is provide a credit or debit card, which we
            will charge directly to that card upon delivery of your items. For
            your records, we will send a payment confirmation within 24 hours of
            processing your product delivery to the email on your account.
          </Div>
          {showWoops && (
            <Note className={styles.warningNote}>
              <img src={warning} alt="" />
              <F translationKey={'WhoopsNoPaymentMethod'}>
                Whoops! You don&apos;t have a payment method saved.
              </F>
            </Note>
          )}
          <H2>Choose payment method</H2>
          <div className={styles.paymentMethods}>
            <RadioGroup
              className={styles.radioGroup}
              value={pickedPaymentMethod}
              onChange={handleChange}
              row
            >
              {props.creditCards.map((creditCard) =>
                renderPaymentOption({ creditCard, type: 'creditCard' }),
              )}
            </RadioGroup>
            <AddButton
              className={styles.addButton}
              onClick={showCreditCardForm}
              ariaLabel="Add a new credit/debit card"
              dataTracking="Add Card"
            >
              <F>Add a new credit/debit card</F>
            </AddButton>
          </div>
          <Div className={styles.note} translationKey={'NoteSigningUpAutoPay'}>
            Note: By consenting to AutoPay you are authorizing us to
            automatically charge the credit or debit card you have provided to
            us upon each delivery made for your account.
          </Div>
          <div className={styles.buttons}>
            <Button
              text="Cancel"
              color="gray"
              className={styles.cancel}
              onClick={props.onClose}
            />
            <Button
              text="Sign up"
              disabled={!pickedPaymentMethod}
              data-tracking="Auto Pay Sign Up"
              onClick={signUpAutopay}
            />
          </div>
          <EditCreditCardDrawer
            drawerProps={{ onClose: props.onClose }}
            formProps={{ isNew: true }}
          />
          <EditBankAccountDrawer
            drawerProps={{ onClose: props.onClose }}
            formProps={{ isNew: true }}
          />
        </>
      ) : (
        <div className={styles.successContainer}>
          <SuccessBlock text="Your changes have been saved." />
        </div>
      )}
    </Fragment>
  );
};

AutopaySignUp.propTypes = {
  creditCards: PropTypes.arrayOf(PropTypes.object).isRequired,
  bankAccounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentPaymentMethod: PropTypes.object,
  fetchPaymentMethods: PropTypes.func.isRequired,
  showDrawer: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  updateCreditCard: PropTypes.func.isRequired,
  resetAddCardSucessful: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  creditCards: selectCreditCards(),
  bankAccounts: selectBankAccounts(),
  currentPaymentMethod: selectCurrentPaymentMethod(),
});

const mapDispatchToProps = {
  fetchPaymentMethods,
  showDrawer,
  updateCreditCard,
  resetAddCardSucessful,
};

export default connect(mapStateToProps, mapDispatchToProps)(AutopaySignUp);
